import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { cloneDeep } from "lodash";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getPreviewOwnershipDocumentTemplate } from "../../document-templates/PreviewOwnershipDocumentTemplate";
import { OwnershipDocument } from "../../models/OwnershipDocument";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { Utility } from "../../core/Utility";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllPartners } from "../../services/PartnerService";
import {
  getAllOwnershipDocuments,
  deleteOneOwnershipDocument,
} from "../../services/OwnershipDocumentService";
import "./OwnershipDocumentsPage.scss";

export const OwnershipDocumentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // Za tekuću godinu, postavi datume od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      // Za prethodne godine, postavi ceo opseg te godine
      return { from: yearStart, to: yearEnd };
    }
  };

  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [ownershipDocumentsApi, setOwnershipDocumentsApi] = useState<
    OwnershipDocument[]
  >([]);
  const [processing, setProcessing] = useState<boolean>(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    {} as Warehouse
  );
  const [selectedPartner, setSelectedPartner] = useState<Partner>(
    {} as Partner
  );
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>();
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product
  );

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");

  const [ownershipDocumentToPrint, setOwnershipDocumentToPrint] = useState<
    OwnershipDocument[]
  >([]);
  const [pdfData, setPdfData] = useState<any>(
    getPreviewOwnershipDocumentTemplate(
      ownershipDocumentToPrint,
      selectedWarehouse?.name,
      selectedProduct?.name,
      selectedProductCategory?.name ?? "",
      dateFrom,
      dateTo
    )
  );

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    ownerDocNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "warehouse.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "previousOwner.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "newOwner.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "product.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
    srps: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const toast = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshAll = async () => {
    setProcessing(true);
    try {
      const [wResp, pResp, pcResp, partnerResp, odResp] = await Promise.all([
        getAllWarehouses(),
        getAllProducts(),
        getAllProductCategories(),
        getAllPartners(),
        getAllOwnershipDocuments(),
      ]);

      [wResp, pResp, pcResp, partnerResp, odResp].forEach(ensureSuccess);

      setWarehousesApi(wResp?.data?.result ?? []);
      setProductsApi(pResp?.data?.result ?? []);
      setProductCategoriesApi(pcResp?.data?.result ?? []);
      setPartnersApi(partnerResp?.data?.result ?? []);
      setOwnershipDocumentsApi(odResp?.data?.result ?? []);
    } finally {
      setProcessing(false);
    }
  };

  const refreshOwnershipDocuments = async () => {
    setProcessing(true);
    try {
      const odResp = await getAllOwnershipDocuments();
      ensureSuccess(odResp);
      setOwnershipDocumentsApi(odResp?.data?.result ?? []);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    refreshAll()
      .then(() => {
        formatOwnershipDocumentToPrint();
      })
      .catch((e: any) => {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    formatOwnershipDocumentToPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownershipDocumentsApi,
    selectedWarehouse,
    selectedPartner,
    selectedProductCategory,
    selectedProduct,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    formatPdfData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownershipDocumentToPrint]);

  const formatOwnershipDocumentToPrint = () => {
    const warehouse = warehousesApi.find(
      (item) => item.id == selectedWarehouse.id
    );
    const warehouseName = warehouse?.name;
    const product = productsApi.find((item) => item.id == selectedProduct.id);
    const productName = product?.name;
    const partner = partnersApi.find((item) => item.id == selectedPartner.id);
    const partnerName = partner?.name;
    const dateFromFormated = new Date(dateFrom).getTime();
    const dateToFormated = new Date(dateTo).getTime() + 1000 * 60 * 60 * 24; //kraj dana
    let copyOfOwnershipDocumentToPrint: OwnershipDocument[] = [];
    for (const oneDocument of ownershipDocumentsApi) {
      //dodavanje ulaznih podataka filtriranih po magacinu, proizvodu, datumOd, datumDo
      if (
        (warehouse === undefined
          ? true
          : oneDocument.warehouse.name === warehouseName) && //...u izabranom magacinu...default za sve magacine
        (partner === undefined
          ? true
          : oneDocument.previousOwner?.name === partnerName) && //...za izabranog dobavljaca...default za sve dobavljace
        (product === undefined
          ? true
          : oneDocument.product?.name === productName) && //...za izabranu robu...default za sve proizvode
        (selectedProductCategory === undefined
          ? true
          : oneDocument.product?.category?.id === selectedProductCategory.id) && //...za izabrane kategorije robe...default za sve kategorije proizvoda
        oneDocument.srps != 0 &&
        new Date(oneDocument?.date as Date).getTime() > dateFromFormated && //...sa datumom vecim od minimalnog datuma...
        new Date(oneDocument?.date as Date).getTime() <= dateToFormated //...i datumom manjim od maksimalnog datuma...
      ) {
        copyOfOwnershipDocumentToPrint.push(oneDocument);
      }
    }
    const sortedOwnershipDocumentToPrint = copyOfOwnershipDocumentToPrint.sort(
      (a: OwnershipDocument, b: OwnershipDocument) =>
        new Date(a?.date as Date).getTime() <
        new Date(b?.date as Date).getTime()
          ? 1
          : -1
    );
    setOwnershipDocumentToPrint(sortedOwnershipDocumentToPrint);
  };

  const formatPdfData = () => {
    // populate pdf when filter changes
    const newPdf = getPreviewOwnershipDocumentTemplate(
      ownershipDocumentToPrint,
      selectedWarehouse.name,
      selectedProduct.name,
      selectedProductCategory?.name ?? "",
      dateFrom,
      dateTo
    );
    setPdfData(newPdf);
  };

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    refreshOwnershipDocuments()
      .then(() => {
        formatOwnershipDocumentToPrint();
      })
      .catch((e: any) => {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            String(t("Greška pri učitavanju vlasničkih listova")),
          life: 3500,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear]);

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse);
  };

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
    setSelectedProduct({} as Product);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = productsApi.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewPartner = cloneDeep(selectedPartner);
    copyOfNewPartner = partnersApi.find((item) => item.id == value) as Partner;
    setSelectedPartner(copyOfNewPartner);
  };

  const accept = async () => {
    try {
      const resp = await deleteOneOwnershipDocument(inputToDelete);
      ensureSuccess(resp);

      setDeleteConfirmVisible(false);
      setInputToDelete("");

      await refreshAll();
      formatOwnershipDocumentToPrint();

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Vlasnički list je obrisan"),
        life: 3000,
      });
    } catch (e: any) {
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: e?.response?.data?.message ?? String(t("Greška pri brisanju")),
        life: 3500,
      });
    }
  };

  const exportExcel = () => {
    const excelOwnershipDocument = ownershipDocumentToPrint.map(
      (item: OwnershipDocument) => {
        return {
          BrojVlLista: item.ownerDocNumberFormatted ?? item.ownerDocId,
          Datum: Utility.formatDate(new Date(item.date as Date)),
          Magacin: item.warehouse.name,
          PrethodniVlasnik: item.previousOwner?.name,
          NoviVlasnik: item.newOwner?.name,
          Roba: item.product?.name,
          Neto: item.netto,
          Srps: item.srps,
          Vlaga: parseFloat((item.moisture ?? 0)?.toString()),
          Primese: parseFloat((item.impurity ?? 0)?.toString()),
          Defekt: parseFloat((item.defect ?? 0)?.toString()),
          Lom: parseFloat((item.breakage ?? 0)?.toString()),
          Hektolitar: parseFloat((item.hectoliter ?? 0)?.toString()),
          Protein: parseFloat((item.protein ?? 0)?.toString()),
        };
      }
    );
    const excelObject = [
      {
        BrojVlLista: "",
        Datum: "",
        Magacin: "",
        PrethodniVlasnik: "",
        NoviVlasnik: "",
        Roba: "",
        Neto: "",
        Srps: "",
        Vlaga: "",
        Primese: "",
        Defekt: "",
        Lom: "",
        Hektolitar: "",
        Protein: "",
      },
      ...excelOwnershipDocument,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Vlasnički listovi merkantile ${Utility.formatDate(new Date())}`
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const ownerDocNoBody = (row: OwnershipDocument) => {
    return <span>{row.ownerDocNumberFormatted || row.ownerDocId || ""}</span>;
  };

  const dateBody = (row: OwnershipDocument) => {
    return row?.date ? (
      <span>{Utility.formatDate(new Date(row.date as any))}</span>
    ) : (
      <></>
    );
  };

  const warehouseBody = (row: OwnershipDocument) => (
    <span>{String(row?.warehouse?.name ?? "").toUpperCase()}</span>
  );

  const previousOwnerBody = (row: OwnershipDocument) => (
    <span>{row?.previousOwner?.name ?? ""}</span>
  );

  const newOwnerBody = (row: OwnershipDocument) => (
    <span>{row?.newOwner?.name ?? ""}</span>
  );

  const productBody = (row: OwnershipDocument) => (
    <span>{row?.product?.name ?? ""}</span>
  );

  const nettoBody = (row: OwnershipDocument) => (
    <span>
      {row?.netto != null ? Utility.addThousandSeparator(row.netto as any) : ""}
    </span>
  );

  const srpsBody = (row: OwnershipDocument) => (
    <span>
      {row?.srps != null
        ? Utility.addThousandSeparator(parseInt(String(row.srps)))
        : ""}
    </span>
  );

  const actionsBody = (row: OwnershipDocument) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => navigate(`/ownership-document/${row.id}`)}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-trash"
          onButtonClick={() => {
            setInputToDelete(row.id);
            setDeleteConfirmVisible(true);
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file-text header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            />
            <span className="header-left-title">
              {t("Vlasnički listovi merkantile")}
            </span>
            <span className="header-elements header-left-title">{`${t(
              "za"
            )}`}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
            />
            <span className="header-elements header-left-title">{`${t(
              "od"
            )}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />
            <span className="header-elements header-left-title">{`${t(
              "do"
            )}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />
            <span className="header-elements header-left-title">{`${t(
              "za"
            )}`}</span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategoriesApi.filter(
                (item) => item.type == ProductType.MercantileGoods
              )}
              filter={false}
              labelText=""
              placeholderText={t("vrstu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
            />
            <span className="header-elements"> </span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.category?.id === selectedProductCategory?.id
              )}
              filter={false}
              labelText=""
              placeholderText={t("Ime")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory === undefined}
            />
            <span className="header-elements header-left-title">dobavljač</span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPartner?.id}
              onValueChange={handlePartnerChange}
              optionValue="id"
              optionLabel="name"
              options={partnersApi}
              filter={true}
              labelText=""
              placeholderText={t("Dobavljač")}
              control={control}
              setValue={setValue}
              errors={errors}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const { from, to } = getDefaultRangeForBusinessYear();
                setDateFrom(from);
                setDateTo(to);
                setSelectedWarehouse({} as Warehouse);
                setSelectedProductCategory(undefined);
                setSelectedProduct({} as Product);
                setSelectedPartner({} as Partner);
              }}
            />
          </div>

          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportExcel}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem 0 2rem",
              }}
              labelText={t("Nov vlasnički list")}
              icon="bi bi-file-text"
              onButtonClick={() => navigate(`/ownership-document/${v4()}`)}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={ownershipDocumentToPrint}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema vlasničkih listova za zadate kriterijume")}
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
            >
              <Column
                field="ownerDocNumberFormatted"
                header={t("Broj vl. lista")}
                body={ownerDocNoBody}
                sortable
                filter
                style={{ minWidth: "160px" }}
              />
              <Column
                field="date"
                header={t("Datum")}
                body={dateBody}
                sortable
                filter
                style={{ minWidth: "140px" }}
              />
              <Column
                header={t("Magacin")}
                body={warehouseBody}
                sortable={false}
                filter
                filterField="warehouse.name"
                style={{ minWidth: "220px" }}
              />
              <Column
                header={t("Prethodni vlasnik")}
                body={previousOwnerBody}
                filter
                filterField="previousOwner.name"
                style={{ minWidth: "220px" }}
              />
              <Column
                header={t("Novi vlasnik")}
                body={newOwnerBody}
                filter
                filterField="newOwner.name"
                style={{ minWidth: "220px" }}
              />
              <Column
                header={t("Naziv robe")}
                body={productBody}
                filter
                filterField="product.name"
                style={{ minWidth: "220px" }}
              />
              <Column
                field="netto"
                header={t("Neto")}
                body={nettoBody}
                sortable
                filter
                style={{ minWidth: "140px" }}
              />
              <Column
                field="srps"
                header={t("Srps")}
                body={srpsBody}
                sortable
                filter
                style={{ minWidth: "140px" }}
              />
              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "140px" }}
              />
            </DataTable>
          </div>
        )}
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t(
              "Vlasnički listovi merkantile"
            )}_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da obrišete vlasnički list?")}
        header={t("Potvrda brisanja vlasničkog lista")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
    </PageLayout>
  );
};
